import { AfterViewInit, Component, EventEmitter, Input, OnInit, Optional, Output, ViewChild } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { AuthType } from '@core/enums/auth.enum';
import { Translations } from '@core/models/translations.model';
import { environment as env } from '@environments/environment';
import { ButtonType } from '@shared/components/button/button.component';
import { SnackService } from '@shared/services/snack.service';
import { DeviceDetectorService } from 'ngx-device-detector';
import { BehaviorSubject, Observable, map, takeWhile, timer } from 'rxjs';
import { AuthDialogComponent } from '../../auth-dialog/auth-dialog.component';
import { AuthFacade } from '../../auth.facade';
import { LoginComponent } from '../login/login.component';
import { CookieService } from 'ngx-cookie-service';
import { InputComponent } from '@shared/components/form/input/input.component';

@Component({
	selector: 'app-otp',
	templateUrl: './otp.component.html',
	styleUrls: ['./otp.component.scss'],
})
export class OtpComponent implements OnInit, AfterViewInit {
	constructor(
		@Optional() private readonly dialogRef: MatDialogRef<AuthDialogComponent>,
		private readonly fb: FormBuilder,
		private readonly authFacade: AuthFacade,
		private readonly snackService: SnackService,
		private readonly route: ActivatedRoute,
		private readonly deviceDetectorService: DeviceDetectorService,
		private cookie: CookieService
	) {}

	@Input() phone: string;
	@Input() phonePrefix: string;
	@Input() phoneWithCountryCode: string;
	@Input() translations: Translations;
	@Input() authType = AuthType.LOGIN;
	@Input() timeStart: number;

	@Output() changed = new EventEmitter();
	@Output() handleLoginSuccess = new EventEmitter<void>();
	@ViewChild('otpInput') otpInput!: InputComponent; // assuming 'otpInput' is the ID passed to app-input

	form: FormGroup;
	otpTimeRemaining$: Observable<number>;
	isLoading = false;
	isResending = false;
	canCallForHelp = false;
	canChangeNumber = false;
	btnVerifyLabel = 'n540';
	btnResendLabel = 'n545';

	readonly authTypeEnum = AuthType;
	readonly buttonType = ButtonType;
	startTime = 90;
	remainingTime: number;

	// Easy getter for the form controls
	get fc(): { [p: string]: AbstractControl } {
		return this.form.controls;
	}

	async onVerifyOtp(): Promise<void> {
		const otpCode = this.fc.otp.value;

		this.isLoading = true;
		this.btnVerifyLabel = 'n544';

		try {
			switch (this.authType) {
				case AuthType.LOGIN:
					await this.login(otpCode);

					break;
				case AuthType.VERIFY_OLD_MOBILE:
					await this.verifyOldMobile(otpCode);

					break;

				case AuthType.VERIFY_NEW_MOBILE:
					await this.verifyNewMobile(otpCode);

					break;

				case AuthType.DELETE_ACCOUNT:
					await this.deleteAccount(otpCode);

					break;
				default:
			}
		} catch (error) {
			// Add error handling here...
		} finally {
			this.isLoading = false;
			this.btnVerifyLabel = 'n540';
		}
	}

	async onResendCode(): Promise<void> {
		this.isResending = true;
		this.canCallForHelp = true;
		this.btnResendLabel = 'n571';

		// added this currently extra here the. remove this when TWILIO CODE is added back

		const phoneWithCountryCode = `${this.phonePrefix}${this.phone.substring(1)}`;

		try {
			//FIREBASE CODE
			// await this.authFacade.signInWithPhoneNumber(phoneWithCountryCode);

			// TWILIO CODE
			await this.authFacade.signInWithPhoneNumberTwilio({
				phone: this.phone,
				phonePrefix: this.phonePrefix,
			});
			this.getOTPTimer(this.phone);
		} catch (error) {
			this.snackService.open(this.translations['n270'], 5000, null, 'error'); // remove this after TWILIO is back
		} finally {
			this.isLoading = false;
			this.isResending = false;
			this.btnResendLabel = 'n545';
		}

		// TWILIO CODE
		// try {
		// 	await this.authFacade.signInWithPhoneNumberTwilio({
		// 		phone: this.phone,
		// 		phonePrefix: this.phonePrefix,
		// 	});

		// 	// TODO: Need translations
		// 	this.snackService.open(this.translations['n573']);
		// } catch (error) {
		// 	// Add error handling here...
		// 	console.error(error);
		// } finally {
		// 	this.isResending = false;
		// 	this.btnResendLabel = 'n545';
		// }

		// this.startTimer();
	}

	/**
	 * Go back to Enter new number screen
	 * TODO: It's better to change the LoginComponent to EnterMobileComponent?
	 */
	onChangeNumber(): void {
		this.changed.emit({
			component: LoginComponent,
			authType: this.authType,
		});
	}

	/**
	 * Redirect to WhatsApp
	 */
	onOpenWhatsApp(): void {
		const targetDisplay = this.deviceDetectorService.isMobile() ? '_self' : '_blank';

		window.open(env.WHATSAPP.callForHelpUrl, targetDisplay);
	}

	private async deleteAccount(otpCode: string): Promise<void> {
		await this.authFacade.deleteAccount({
			phone: this.phone,
			phonePrefix: this.phonePrefix,
			pin: otpCode,
		});

		this.snackService.open(this.translations['n584'], 5000);

		this.dialogRef.close();
	}

	/**
	 * Update new phone number
	 *
	 * @param otpCode
	 */
	private async verifyNewMobile(otpCode: string): Promise<void> {
		await this.authFacade.updateMobile({
			phone: this.phone,
			phonePrefix: this.phonePrefix,
			pin: otpCode,
		});

		this.snackService.open(this.translations['n550']);

		this.dialogRef.close();
	}

	/**
	 * Login/Register new phone number
	 *
	 * Verify first the otp code, then go to login/register process.
	 *
	 * @param otpCode
	 */
	private async login(otpCode: string): Promise<void> {
		const queryParams = this.route.snapshot.queryParams;
		/**
		 * Check if placing an ad. Then add `assignAd`
		 *
		 * TODO: Rename the`pa` as this not readable.
		 */
		const isPlacingAd = queryParams?.from === 'pa';
		const assignAd = queryParams?.id;

		await this.authFacade.login(otpCode, { phone: this.phone, phonePrefix: this.phonePrefix, ...(isPlacingAd && { assignAd }) });

		if (this.dialogRef) {
			this.dialogRef.close();
		}

		this.handleLoginSuccess.emit();

		//on success Delete cookie stored for checking same number login within 6 seconds
		this.cookie.delete('phoneNumber');
		this.cookie.delete('startTimeStamp');

		// TODO: Changed this with translations
		this.snackService.open(this.translations['n564']);
	}

	/**
	 * Will go to `Enter new number` screen to enter a new mobile number
	 *
	 * @param otpCode
	 */
	private async verifyOldMobile(otpCode: string): Promise<void> {
		await this.authFacade.verifyOldMobile({
			phone: this.phone,
			phonePrefix: this.phonePrefix,
			pin: otpCode,
		});

		this.changed.emit({
			component: LoginComponent,
			authType: AuthType.VERIFY_NEW_MOBILE,
		});
	}

	private createForm(): void {
		this.form = this.fb.group({
			otp: ['', [Validators.required, Validators.minLength(6), Validators.pattern('[0-9]+')]],
		});
	}

	private async verifyMobile(): Promise<void> {
		const phoneWithoutCountryCode = this.phone;
		const phonePrefix = this.phonePrefix;
		const phoneWithCountryCode = `${phonePrefix}${phoneWithoutCountryCode.substring(1)}`;

		this.isLoading = true;

		/**
		 * Firebase phone authentication first then fallback error to Twilio authentication.
		 */
		try {
			//FIREBASE CODE
			// await this.authFacade.signInWithPhoneNumber(phoneWithCountryCode);

			//TWILIO CODE
			await this.authFacade.signInWithPhoneNumberTwilio({
				phone: phoneWithoutCountryCode,
				phonePrefix: phonePrefix,
			});
			this.startTimer();
		} catch (error) {
			this.snackService.open(this.translations['n270'], 5000, null, 'error'); // remove this after TWILIO is back

			// TWILIO CODE
			// await this.authFacade
			// 	.signInWithPhoneNumberTwilio({
			// 		phone: phoneWithoutCountryCode,
			// 		phonePrefix: phonePrefix,
			// 	})
			// 	.then(() => this.startTimer())
			// 	.catch(() => this.snackService.open(this.translations['n174'], 5000, null, 'error'))
			// 	.finally(() => (this.isLoading = false));
			// this.snackService.open(this.translations['n174'], 5000, null, 'error');
		} finally {
			this.isLoading = false;
		}
	}

	private startTimer(): void {
		this.otpTimeRemaining$ = timer(0, 1000).pipe(
			map((time: number) => (this.remainingTime - time) * 1000),
			takeWhile((time: number) => time >= 0)
		);
	}

	private initDeclarations(): void {
		this.canChangeNumber = this.authType === AuthType.VERIFY_NEW_MOBILE || this.authType === AuthType.LOGIN;
	}

	private handleInitDeleteAccount(): void {
		/**
		 * Don't start the timer on init of account deletion.
		 * Start the timer after the phone authentication (signInWithPhoneNumber) is done.
		 */
		if (this.authType !== AuthType.DELETE_ACCOUNT) {
			this.startTimer();
		}
	}

	private handleInitAuthenticationDeleteAccount(): void {
		/**
		 * In account deletion, send the OTP code on init.
		 */
		if (this.authType === AuthType.DELETE_ACCOUNT) {
			setTimeout(() => {
				this.verifyMobile();
			});
		}
	}

	/*
	Function to set otp timer now 
	*/
	private getOTPTimer(phoneNumber: string, startTimeStamps?: number) {
		if (this.authType === AuthType.LOGIN) {
			const storedPhoneNumber = this.cookie.get('phoneNumber');
			const startTimeStamp = startTimeStamps ? startTimeStamps : parseInt(this.cookie.get('startTimeStamp'), 10);
			let currentTimeStamp = Math.floor(Date.now() / 1000);

			if (phoneNumber !== storedPhoneNumber || isNaN(startTimeStamp) || currentTimeStamp - startTimeStamp >= 90) {
				this.cookie.set('phoneNumber', phoneNumber);
				this.cookie.set('startTimeStamp', currentTimeStamp.toString());
				this.remainingTime = 90;
			}

			const elapsedTime = currentTimeStamp - parseInt(this.cookie.get('startTimeStamp'), 10);
			this.remainingTime = Math.max(90 - elapsedTime, 0);

			const countdownInterval = setInterval(() => {
				currentTimeStamp = Math.floor(Date.now() / 1000); // Update the current timestamp

				const elapsedTime = currentTimeStamp - parseInt(this.cookie.get('startTimeStamp'), 10);
				this.remainingTime = Math.max(90 - elapsedTime, 0);

				if (this.remainingTime > 0) {
					console.log(this.remainingTime + ' seconds left');
				} else {
					clearInterval(countdownInterval);
					this.cookie.delete('phoneNumber');
					this.cookie.delete('startTimeStamp');
				}
			}, 1000);
		} else {
			this.remainingTime = 90;
			const countdownInterval = setInterval(() => {
				if (this.remainingTime > 0) {
					this.remainingTime--;
				} else {
					clearInterval(countdownInterval);
				}
			}, 1000);
		}
	}
	ngOnInit(): void {
		this.createForm();
		this.getOTPTimer(this.phone, this.timeStart);

		this.handleInitDeleteAccount();
		this.initDeclarations();
	}

	ngAfterViewInit(): void {
		this.handleInitAuthenticationDeleteAccount();
		if (this.otpInput) {
			this.otpInput.focusOnInput();
		}
	}
}
